import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataGrid, { Column, Paging, FilterRow, Selection, Sorting } from "devextreme-react/data-grid";
import LoadIndicator from "devextreme-react/load-indicator";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import 'devextreme/dist/css/dx.material.blue.light.css';
import MultiWaferMapCanvas from "../components/MultiWaferMapCanvas";
import InteractiveWaferMap from "../components/InteractiveWaferMap";
import createMultiWaferSVG from "../utils/createMultiWaferSVG";
import API_URL from '../config';

export default function LotWaferView({ user }) {
  const [dateFrom, setDateFrom] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dateTo, setDateTo] = useState(new Date());
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [allWaferData, setAllWaferData] = useState([]);
  const [uniqueLots, setUniqueLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedWaferKeys, setSelectedWaferKeys] = useState([]); 
  const [mapsToDraw, setMapsToDraw] = useState([]);
  const [loading, setLoading] = useState({ lot: false, device: false, map: false });
  const [error, setError] = useState("");
  const [zoomedInMap, setZoomedInMap] = useState(null);

  const formatDate = (date) => {
    if (!date) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  };

  // CUST 목록 로드
  useEffect(() => {
    (async () => {
      setLoading(prev => ({ ...prev, device: true }));
      try {
        const res = await axios.get(`${API_URL}/api/prb/distinctCust`);
        const list = res.data.data || [];
        const mappedList = list.map(item => ({ CODE_CD: item.CUST, NAME: item.CUST }));
        setCustList(mappedList);

        if (user?.role === "CUST") {
          const me = mappedList.find(i => i.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
          if (me) setSelectedCust(me.CODE_CD);
          else setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
        }
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setLoading(prev => ({ ...prev, device: false }));
      }
    })();
  }, [user]);

  // DEVICE 목록 로드 (함수형 업데이트 적용으로 ESLint 경고 해결)
  useEffect(() => {
    if (!selectedCust) {
      setDeviceList([]);
      setSelectedDevice(null);
      return;
    }
    (async () => {
      setLoading(prev => ({ ...prev, device: true }));
      try {
        const res = await axios.get(`${API_URL}/api/prb/distinctDevice`, { 
          params: { 
            cust: selectedCust,
            startDate: formatDate(dateFrom),
            endDate: formatDate(dateTo)
          } 
        });
        const list = res.data.data || [];
        const formattedList = list.map(r => ({ CODE_CD: r.DEVICE, NAME: r.DEVICE }));
        
        // "ALL" 옵션 추가
        const finalDeviceList = [{ CODE_CD: "ALL", NAME: "ALL" }, ...formattedList];
        setDeviceList(finalDeviceList);

        // 함수형 업데이트(prev)를 사용하여 의존성 배열 추가 없이 이전 값 판단 및 업데이트 실행
        setSelectedDevice(prev => {
          if (prev && finalDeviceList.some(d => d.CODE_CD === prev)) {
            return prev; // 기존 선택값 유지
          }
          return "ALL"; // 매치되지 않거나 없을 경우 기본값 "ALL" 지정
        });
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setLoading(prev => ({ ...prev, device: false }));
      }
    })();
  }, [selectedCust, dateFrom, dateTo]); // selectedDevice를 종속성에서 안전하게 제외함

  useEffect(() => {
    setSelectedWaferKeys([]);
  }, [selectedLot]);

  // Wafer 목록 조회
  const fetchLotData = useCallback(async () => {
    if (!selectedCust || !selectedDevice) { setError("CUST와 DEVICE를 모두 선택해주세요."); return; }
    setLoading(prev => ({ ...prev, lot: true }));
    setError("");
    setAllWaferData([]);
    setUniqueLots([]);
    setSelectedLot(null); 
    setSelectedWaferKeys([]);
    setMapsToDraw([]);
    try {
      const res = await axios.get(`${API_URL}/api/prb/waferSummaryList`, { 
        params: { 
          startDate: formatDate(dateFrom), 
          endDate: formatDate(dateTo), 
          cust: selectedCust, 
          device: selectedDevice 
        } 
      });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg || "데이터 조회 실패");
      
      const allWafers = (res.data.data || []).map(item => ({
        ...item,
        LOT_ID: item.LOT_NO,
        TESTAREA: item.OPER,
        WAFER_SEQ: item.WAFER_ID,
        MAP_TABLE_NAME: `T_DEVICE_${item.DEVICE.toUpperCase()}`
      }));
      
      setAllWaferData(allWafers);

      const lotSet = new Set();
      const uniqueLotList = allWafers.filter(item => { 
        const uniqueKey = `${item.LOT_ID}_${item.TESTAREA}`; 
        if (lotSet.has(uniqueKey)) return false; 
        lotSet.add(uniqueKey); 
        return true; 
      }).map(item => ({ 
        id: `${item.LOT_ID}_${item.TESTAREA}`, 
        LOT_ID: item.LOT_ID, 
        DEVICE: item.DEVICE, 
        TESTAREA: item.TESTAREA || '', 
      }));

      setUniqueLots(uniqueLotList);
      if (uniqueLotList.length === 0) { setError("조회된 데이터가 없습니다."); }
    } catch (e) {
      setError(`조회 오류: ${e.message}`);
    } finally {
      setLoading(prev => ({ ...prev, lot: false }));
    }
  }, [dateFrom, dateTo, selectedCust, selectedDevice]);

  // Wafer Map 조회 및 속성 매핑 (대소문자 양식 및 좌표 대응 적용)
  const handleDrawMaps = useCallback(async () => {
    if (selectedWaferKeys.length === 0) { setError("먼저 그리드에서 Wafer를 선택해주세요."); return; }
    if (selectedWaferKeys.length > 25) { setError("한 번에 최대 25개의 Wafer만 선택할 수 있습니다."); return; }
    setLoading(prev => ({ ...prev, map: true }));
    setError("");
    setMapsToDraw([]);

    const wafersToFetch = allWaferData.filter(wafer => selectedWaferKeys.includes(wafer.WAFER_SEQ));
    
    // 디바이스가 ALL인 상황에 대응하기 위해 디바이스별 데이터 매핑 그룹화
    const deviceGroups = wafersToFetch.reduce((acc, wafer) => {
      const dev = wafer.DEVICE;
      if (!acc[dev]) acc[dev] = [];
      acc[dev].push(wafer.WAFER_SEQ);
      return acc;
    }, {});

    try {
      const mapPromises = Object.keys(deviceGroups).map(dev => 
        axios.post(`${API_URL}/api/prb/waferMapFromDeviceTable`, {
          device: dev,
          waferIds: deviceGroups[dev]
        })
      );
      
      const responses = await Promise.all(mapPromises);
      
      let mergedData = {};
      responses.forEach(res => {
        if (res.data.retCode === 0 && res.data.data) {
          mergedData = { ...mergedData, ...res.data.data };
        }
      });

      const newMapsToDraw = wafersToFetch.map(wafer => {
        const rawMapData = mergedData[wafer.WAFER_ID] || [];
        
        // 캔버스 모듈 내에서 속성 인식이 누락되지 않도록 다각도로 매핑 지원
        const mapData = rawMapData.map(d => ({
          X: d.X !== undefined ? d.X : d.X_POS,
          Y: d.Y !== undefined ? d.Y : d.Y_POS,
          SBIN: d.SBIN,
          X_POS: d.X_POS !== undefined ? d.X_POS : d.X,
          Y_POS: d.Y_POS !== undefined ? d.Y_POS : d.Y,
          x: d.X !== undefined ? d.X : d.X_POS,
          y: d.Y !== undefined ? d.Y : d.Y_POS,
          sbin: d.SBIN
        }));

        let calculatedYld = 0;
        if (mapData.length > 0) { 
          const totalDies = mapData.length; 
          const bin1Count = mapData.filter(d => d.SBIN === 1 || d.sbin === 1).length; 
          calculatedYld = (bin1Count / totalDies) * 100; 
        }

        return { 
          waferInfo: wafer, 
          mapData: { data: mapData, yld: calculatedYld } 
        };
      });

      setMapsToDraw(newMapsToDraw);
    } catch (e) {
      setError(`Map 데이터 조회 중 오류 발생: ${e.message}`);
    } finally {
      setLoading(prev => ({ ...prev, map: false }));
    }
  }, [selectedWaferKeys, allWaferData]);

  const handleLotFocusChanged = (e) => { if (e.row && e.row.data) { setSelectedLot(e.row.data); } };
  const handleWaferSelectionChanged = (e) => { setSelectedWaferKeys(e.selectedRowKeys); };
  
  const wafersForSelectedLot = useMemo(() => { 
    if (!selectedLot) return []; 
    return allWaferData.filter(w => w.LOT_ID === selectedLot.LOT_ID && w.TESTAREA === selectedLot.TESTAREA); 
  }, [selectedLot, allWaferData]);
  
  const handleExportAllSVG = () => { createMultiWaferSVG(mapsToDraw); };
  const handleMapClick = (mapData) => { setZoomedInMap(mapData); };
  const handleCloseModal = () => { setZoomedInMap(null); };

  return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 40px)', padding: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
      <div style={{ flex: '0 0 750px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {loading.lot && ( <div className="loading-overlay"><div className="loading-spinner"></div></div> )}
        <h4>Wafer Selection</h4>
        <div style={{ marginBottom: 10, padding: 10, border: "1px solid #eee", borderRadius: "4px" }}>
          <div style={{ display: "flex", flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <DateBox value={dateFrom} onValueChanged={(e) => setDateFrom(e.value)} label="From" width={200} />
            <DateBox value={dateTo} onValueChanged={(e) => setDateTo(e.value)} label="To" width={200} />
            <SelectBox dataSource={custList} value={selectedCust} onValueChanged={(e) => setSelectedCust(e.value)} valueExpr="CODE_CD" displayExpr="NAME" placeholder="CUST" disabled={user?.role === "CUST"} searchEnabled width={200} />
            <SelectBox dataSource={deviceList} value={selectedDevice} onValueChanged={(e) => setSelectedDevice(e.value)} valueExpr="CODE_CD" displayExpr="NAME" placeholder="DEVICE" disabled={!selectedCust || loading.device} searchEnabled width={250} />
            <div style={{ flex: '1 1 auto', display: 'flex', gap: 10, justifyContent: 'flex-start', paddingTop: '20px' }}>
              <button onClick={fetchLotData} disabled={loading.lot} style={{ padding: "8px 16px", background: "#007acc", color: "white", border: "none", borderRadius: "4px", height: '36px', cursor: 'pointer' }}>
                {loading.lot ? <LoadIndicator width={20} height={20} /> : "Search"}
              </button>
              <button onClick={handleDrawMaps} disabled={loading.map || selectedWaferKeys.length === 0} style={{ padding: "8px 16px", background: "#28a745", color: "white", border: "none", borderRadius: "4px", height: '36px', cursor: 'pointer' }}>
                {loading.map ? "Loading..." : `Draw MAP (${selectedWaferKeys.length})`}
              </button>
              <button onClick={handleExportAllSVG} disabled={mapsToDraw.length === 0} style={{ padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", height: '36px', cursor: 'pointer' }}>
                Save All as SVG
              </button>
            </div>
          </div>
        </div>
        {error && <p style={{ color: "red", margin: "0 0 10px 0" }}>{error}</p>}
        <div style={{ display: 'flex', flex: 1, gap: '10px', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', minHeight: 0 }}>
          <div style={{ flex: 1, height: '500px', display: 'flex', flexDirection: 'column' }}>
            <h5>Lots ({uniqueLots.length})</h5>
            <DataGrid dataSource={uniqueLots} keyExpr="id" height="100%" showBorders columnAutoWidth hoverStateEnabled focusedRowEnabled onFocusedRowChanged={handleLotFocusChanged}>
              <Paging defaultPageSize={100} /><FilterRow visible={true} /><Column dataField="DEVICE" width={180}/><Column dataField="TESTAREA" /><Column dataField="LOT_ID" />
            </DataGrid>
          </div>
          <div style={{ flex: 1, height: '500px', display: 'flex', flexDirection: 'column' }}>
            <h5>Wafers in Lot: {selectedLot?.LOT_ID || 'N/A'} ({selectedLot?.TESTAREA || ''})</h5>
            <DataGrid dataSource={wafersForSelectedLot} keyExpr="WAFER_SEQ" height="100%" showBorders columnAutoWidth selectedRowKeys={selectedWaferKeys} onSelectionChanged={handleWaferSelectionChanged}>
              <Selection mode="multiple" showCheckBoxesMode="always" /><Paging defaultPageSize={100} /><Sorting mode="none" /><Column dataField="WAFER_NO" caption="Wafer No" alignment="center" /><Column dataField="WAFER_ID" caption="Wafer ID" visible={false}/><Column dataField="TOTAL_DIE" caption="TOTAL DIE" visible={false} />
            </DataGrid>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, height: 'calc(100vh - 40px)', border: "1px solid #ccc", padding: 10, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ margin: '0 0 10px 0', flexShrink: 0 }}>Wafer Map Result</h4>
        {loading.map && (
          <div style={{ position: 'absolute', top: '50px', left: 10, right: 10, bottom: 10, backgroundColor: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: '4px' }}>
             <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <div className="loading-text">Loading Map...</div>
              </div>
          </div>
        )}
        <div style={{ 
            flex: 1, 
            minHeight: 0, 
            width: '100%', 
            border: '1px solid #eee', 
            display: 'grid', 
            gridTemplateColumns: 'repeat(5, 1fr)', 
            gridTemplateRows: 'repeat(5, 1fr)',
            gap: '5px', 
            padding: '5px',
            overflow: 'auto' 
        }}>
          {mapsToDraw.map((map, index) => (
            <div key={index} style={{border: '1px solid #ccc', borderRadius: '5px', background: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: '150px' }}>
              <h5 style={{textAlign: 'center', margin: '5px 0', flexShrink: 0, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {map.waferInfo.LOT_ID} / {map.waferInfo.WAFER_NO}
              </h5>
              <div style={{flex: 1, minHeight: 0, position: 'relative'}}>
                <MultiWaferMapCanvas mapInfo={map.mapData} onMapClick={() => handleMapClick(map)} />
              </div>
            </div>
          ))}
          {mapsToDraw.length === 0 && Array.from({ length: 25 }).map((_, i) => <div key={`empty-${i}`} style={{border: '1px dashed #eee', borderRadius: '5px', minHeight: '150px'}}></div>)}
        </div>
      </div>
      {zoomedInMap && (
        <div onClick={handleCloseModal} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '80vw', height: '90vh', maxWidth: '1200px', padding: '20px', borderRadius: '8px', backgroundColor: 'white', boxShadow: '0 5px 15px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{marginTop: 0, textAlign: 'center'}}>
              {zoomedInMap.waferInfo.LOT_ID} / {zoomedInMap.waferInfo.WAFER_NO}
              <button onClick={handleCloseModal} style={{float: 'right', border: 'none', background: 'transparent', fontSize: '24px', cursor: 'pointer'}}>×</button>
            </h3>
            <div style={{flex: 1, minHeight: 0}}>
              <InteractiveWaferMap mapInfo={zoomedInMap.mapData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

LotWaferView.defaultProps = { user: { role: 'USER', username: '' } };